import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import { Layout, Menu, BackTop, FloatButton } from 'antd';
import { HomeOutlined, InfoCircleOutlined, FileTextOutlined, EditOutlined, UpOutlined, RocketOutlined} from '@ant-design/icons';
import './App.css';

// 导入页面组件
import Home from './pages/Home';
import WritePost from './pages/WritePost';
import Archive from './pages/Archive';
import About from './pages/About';
import Auth from './pages/Auth';
import NotFound from './pages/NotFound';
import PostDetail from './pages/PostDetail';

const { Header, Content, Footer } = Layout;

// 需要登录的页面包装
const ProtectedRoute = ({ children }) => {
  const isLoggedIn = localStorage.getItem('isLoggedIn') === 'true';
  const location = useLocation();
  
  if (!isLoggedIn) {
    // 保存当前路径，登录后可以跳转回来
    localStorage.setItem('redirectPath', location.pathname);
    return <Navigate to="/auth" replace />;
  }
  return children;
};

// 获取当前选中的菜单项
const getSelectedKey = (pathname) => {
  if (pathname === '/') return 'home';
  if (pathname.startsWith('/archive')) return 'archive';
  if (pathname.startsWith('/write')) return 'write';
  if (pathname.startsWith('/about')) return 'about';
  if (pathname.startsWith('/post')) return 'home'; // 文章详情页也高亮首页
  return 'home';
};

// 粒子背景组件
const ParticlesBackground = () => {
  const [particles, setParticles] = useState([]);

  useEffect(() => {
    // 创建粒子
    const newParticles = Array.from({ length: 30 }).map((_, i) => ({
      id: i,
      left: `${Math.random() * 100}%`,
      size: `${2 + Math.random() * 8}px`,
      duration: `${10 + Math.random() * 20}s`,
      delay: `${Math.random() * 5}s`,
      color: `rgba(255, ${158 + Math.random() * 97}, ${187 + Math.random() * 68}, ${0.3 + Math.random() * 0.4})`
    }));
    setParticles(newParticles);
  }, []);

  return (
    <div className="particles-container">
      {particles.map(particle => (
        <div
          key={particle.id}
          className="particle"
          style={{
            left: particle.left,
            width: particle.size,
            height: particle.size,
            background: particle.color,
            animationDuration: particle.duration,
            animationDelay: particle.delay
          }}
        />
      ))}
    </div>
  );
};

// 滚动进度条组件
const ScrollProgress = () => {
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (window.scrollY / totalHeight) * 100;
      setScrollProgress(progress);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div 
      className="scroll-progress" 
      style={{ transform: `scaleX(${scrollProgress / 100})` }}
    />
  );
};

function App() {
  const location = useLocation();
  const [selectedKeys, setSelectedKeys] = useState([getSelectedKey(location.pathname)]);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    // 当路由变化时更新选中状态
    setSelectedKeys([getSelectedKey(location.pathname)]);
  }, [location.pathname]);

  useEffect(() => {
    // 监听滚动，为header添加阴影
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const menuItems = [
    {
      key: 'home',
      icon: <HomeOutlined />,
      label: <Link to="/">首页</Link>,
    },
    {
      key: 'archive',
      icon: <FileTextOutlined />,
      label: <Link to="/archive">归档</Link>,
    },
    {
      key: 'write',
      icon: <EditOutlined />,
      label: <Link to="/write">写日记</Link>,
    },
    {
      key: 'about',
      icon: <InfoCircleOutlined />,
      label: <Link to="/about">我的</Link>,
    },
  ];

  return (
    <Layout className="layout">
      {/* 粒子背景 */}
      <ParticlesBackground />
      
      {/* 滚动进度条 */}
      <ScrollProgress />
      
      <Header 
        style={{ 
          position: 'fixed', 
          zIndex: 100, 
          width: '100%',
          background: isScrolled 
            ? 'linear-gradient(135deg, rgba(255, 158, 187, 0.95), rgba(255, 158, 216, 0.95))' 
            : 'linear-gradient(135deg, var(--primary-color), var(--accent-color))',
          backdropFilter: isScrolled ? 'blur(10px)' : 'none',
          transition: 'all 0.3s ease',
          boxShadow: isScrolled ? '0 4px 20px rgba(255, 158, 187, 0.2)' : 'none'
        }}
      >
        <div className="logo" style={{ 
          float: 'left', 
          color: 'white', 
          fontSize: '20px', 
          fontWeight: 'bold', 
          marginRight: '40px',
          textShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <span style={{ fontSize: '24px', marginRight: '8px' }}>🌸</span>
          我的日记
        </div>
        <Menu
          theme="dark"
          mode="horizontal"
          selectedKeys={selectedKeys}
          items={menuItems}
          style={{ lineHeight: '64px' }}
        />
      </Header>
      
      <Content style={{ padding: '0 50px', marginTop: 64 }} className="custom-scrollbar">
        <div className="page-transition" style={{ 
          background: '#fff', 
          padding: '30px', 
          minHeight: 380, 
          borderRadius: '16px', 
          boxShadow: '0 10px 30px rgba(255, 158, 187, 0.1)',
          margin: '16px 0',
          border: '1px solid #ffd1e0'
        }}>
          <Routes>
            <Route path="/" element={
              <ProtectedRoute>
                <Home />
              </ProtectedRoute>
            } />
            <Route path="/archive" element={
              <ProtectedRoute>
                <Archive />
              </ProtectedRoute>
            } />
            <Route path="/auth" element={<Auth />} />
            <Route path="/post/:id" element={
              <ProtectedRoute>
                <PostDetail />
              </ProtectedRoute>
            } />
            
            {/* 需要登录的页面 */}
            <Route path="/about" element={
              <ProtectedRoute>
                <About />
              </ProtectedRoute>
            } />
            <Route path="/write" element={
              <ProtectedRoute>
                <WritePost />
              </ProtectedRoute>
            } />
            <Route path="/write/:id" element={
              <ProtectedRoute>
                <WritePost />
              </ProtectedRoute>
            } />
            
            {/* 404页面 */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </div>
      </Content>
      
      <Footer style={{ 
        textAlign: 'center', 
        padding: '40px 50px',
        background: 'linear-gradient(135deg, var(--secondary-color), white)',
        borderTop: '1px solid var(--border-color)'
      }}>
        <div style={{ 
          borderTop: '1px solid rgba(255, 209, 224, 0.5)', 
          paddingTop: '30px',
          color: 'var(--text-secondary)'
        }}>
          <div style={{ fontSize: '20px', marginBottom: '10px', color: 'var(--primary-color)' }}>
            ✨ 我的日记 ✨
          </div>
          <div style={{ marginBottom: '10px' }}>
            © {new Date().getFullYear()} - 用 ❤️ 记录成长
          </div>
          <div style={{ fontSize: '12px', opacity: 0.7 }}>
            记录生活的每一刻，珍藏美好的回忆
          </div>
        </div>
      </Footer>
      
      {/* 回到顶部按钮 */}
      <BackTop>
        <div style={{
          height: 40,
          width: 40,
          lineHeight: '40px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #ff9ebb, #ff9ed8)',
          color: '#fff',
          textAlign: 'center',
          fontSize: 20,
          boxShadow: '0 4px 12px rgba(255, 158, 187, 0.3)'
        }}>
          <UpOutlined />
        </div>
      </BackTop>
      
      {/* 浮动操作按钮 */}
      <FloatButton.Group
        shape="circle"
        style={{ right: 24 }}
        trigger="hover"
        icon={<RocketOutlined />}
      >
        <FloatButton 
          icon={<HomeOutlined />}
          tooltip="回到首页"
          onClick={() => window.location.href = '/'}
        />
        <FloatButton 
          icon={<EditOutlined />}
          tooltip="写日记"
          onClick={() => window.location.href = '/write'}
        />
        <FloatButton.BackTop visibilityHeight={100} />
      </FloatButton.Group>
    </Layout>
  );
}

// 包装 App 组件以使用路由钩子
const AppWrapper = () => (
  <Router>
    <App />
  </Router>
);

export default AppWrapper;